import * as React from 'react';
import { ReactElement } from 'react';

import cx from 'classnames';

import BoxBody from 'components/BoxBody';
import Button, { ButtonColorUnion, ButtonLevel } from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Dialog, { Props as DialogProps } from 'components/Dialog';
import LoadingIndicator from 'components/LoadingIndicator';
import { FormattedMessage } from 'lib/intl';
import createChainedFunction from 'utils/createChainedFunction';
import errorLogger from 'utils/errorLogger';

import './styles/ConfirmDialog.scss';

// TODO
// * Reconcile `action*` & `confirm*` prop & method names
// * Fix `className`s

export type Props = DialogProps & {
  actionButtonColor?: ButtonColorUnion;
  actionButtonText?: React.ReactNode;
  cancelButtonColor?: ButtonColorUnion;
  cancelButtonText?: React.ReactNode;
  cancelButtonLevel?: ButtonLevel;
  cancelEventKey?: string | undefined;
  cancelEventProperties?: any;
  children?: React.ReactNode;
  /** Disable action button */
  confirmDisabled?: boolean;
  confirmEventKey?: string | undefined;
  confirmEventProperties?: any;
  /** `border` between `header` & body */
  divided?: boolean;
  /* deprecated, old ui event tracking */
  eventContext?: string;
  /* deprecated, old ui event tracking */
  eventScreenName?: string;
  hideCancel?: boolean;
  /** Cancel button action */
  onCancel?: () => void;
  /** Action button action */
  onConfirm?: Function;
  preConfirmPromise?: () => Promise<any>;
  onRequestHide?: () => void;
  staticModal?: boolean;
  footer?: ReactElement;
  'data-testid'?: string;
};

type State = {
  loading: boolean;
};

export default class ConfirmDialog extends React.Component<Props, State> {
  state: State = {
    loading: false,
  };

  handleConfirm = () => {
    const { onConfirm } = this.props;
    if (onConfirm) {
      return onConfirm();
    }
    return Promise.resolve();
  };

  handleRequestHide = () => {
    const { onRequestHide } = this.props;
    if (onRequestHide) {
      onRequestHide();
    }
  };

  handleCancelButtonClick = () => {
    const { onCancel } = this.props;
    this.handleRequestHide();

    if (onCancel) {
      onCancel();
    }
  };

  handleConfirmButtonClick = () => {
    const { preConfirmPromise, staticModal } = this.props;
    const emptyPromiseFn = () => Promise.resolve();
    const promiseFn = preConfirmPromise || emptyPromiseFn;
    this.setState({ loading: true });

    if (staticModal !== false) {
      promiseFn()
        .then(this.handleConfirm)
        .then(this.handleRequestHide)
        .catch(errorLogger('Static modal pre confirm failure'));
    } else {
      promiseFn().then(this.handleConfirm).catch(errorLogger('Pre confirm failure'));
    }
  };

  renderBody = () => {
    const {
      actionButtonColor,
      actionButtonText,
      cancelButtonText,
      cancelButtonColor,
      cancelButtonLevel,
      cancelEventKey,
      cancelEventProperties,
      children,
      confirmDisabled,
      confirmEventKey,
      confirmEventProperties,
      eventScreenName,
      hideCancel,
      size,
      footer,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <LoadingIndicator />;
    } else {
      return (
        <div>
          <div className={cx({ 'ConfirmDialog-text': !!children })}>{children}</div>
          <Buttons axis="y">
            <Button
              color={actionButtonColor}
              disabled={confirmDisabled}
              key="confirm-action"
              className="action"
              onClick={this.handleConfirmButtonClick}
              eventElementName="ConfirmDialog-actionButton"
              eventScreenName={eventScreenName}
              eventKey={confirmEventKey}
              eventProperties={confirmEventProperties}
              aria-label={
                typeof actionButtonText === 'string' ? actionButtonText : 'confirm action'
              }
              data-test="confirm-dialog-action-button"
              data-testid="confirm-dialog-action-button"
              data-test-selector="confirm_dialog_action_button"
            >
              {actionButtonText || <FormattedMessage id="common.buttons.yes" />}
            </Button>
            {!hideCancel && (
              <Button
                color={cancelButtonColor}
                level={cancelButtonLevel || (!size || size === 'xsmall' ? 'secondary' : 'text')}
                key="confirm-cancel"
                className="cancel"
                onClick={this.handleCancelButtonClick}
                eventElementName="ConfirmDialog-cancelButton"
                eventKey={cancelEventKey}
                eventProperties={cancelEventProperties}
                eventScreenName={eventScreenName}
                data-testid="cancel-dialog-action-button"
              >
                {cancelButtonText || <FormattedMessage id="common.buttons.no" />}
              </Button>
            )}
            {footer && <div className="ConfirmDialog-footer">{footer}</div>}
          </Buttons>
        </div>
      );
    }
  };

  render() {
    const { className, divided, onCancel, onRequestHide, size, staticModal, ...props } = this.props;
    return (
      <Dialog
        size="xxsmall"
        className={cx('ConfirmDialog', className)}
        onRequestHide={createChainedFunction(onCancel, onRequestHide)}
        {...props}
      >
        <BoxBody divided={divided || false}>{this.renderBody()}</BoxBody>
      </Dialog>
    );
  }
}
